/**
 * Admin dashboard tiles for the Time Clock module.
 *
 * Three cards:
 *   • Pending timesheet approvals  (+ Review link → /admin/timesheets)
 *   • Overdue 12-hour response windows (+ list top 3 ops)
 *   • Live on-duty roster + hours logged this week per investigator
 *
 * Hidden entirely for non-admin roles.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BACKEND_URL } from '../../config/canonical';
import { useAuth } from '../../context/AuthContext';
import { Clock, AlertTriangle, Radio, ChevronRight } from 'lucide-react';

const fmtHM = (secs) => {
  const s = Math.max(0, Math.floor(secs || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${h}h ${String(m).padStart(2, '0')}m`;
};

export default function AdminTimeClockTiles() {
  const { isAdmin, user } = useAuth();
  const navigate = useNavigate();
  const token = typeof window !== 'undefined' ? localStorage.getItem('nexus_token') : null;
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!token || !isAdmin) return;
    setLoading(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/timesheets/stats`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) setStats(await r.json());
    } finally { setLoading(false); }
  }, [token, isAdmin]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!isAdmin) return;
    const t = setInterval(load, 60_000);  // refresh every minute
    return () => clearInterval(t);
  }, [isAdmin, load]);

  if (!isAdmin) return null;

  const pending = stats?.pending_approvals_count ?? 0;
  const overdue = stats?.overdue_response_count ?? 0;
  const onDutyRows = stats?.on_duty || [];
  const weekRows = (stats?.hours_this_week || []).slice(0, 5);

  const tileBase = 'nx-panel';
  const tileStyle = { padding: 14, display: 'flex', flexDirection: 'column', minHeight: 180 };

  return (
    <div
      data-testid="admin-timeclock-tiles"
      style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', marginBottom: 14 }}
    >
      {/* Pending approvals */}
      <div className={tileBase} style={tileStyle} data-testid="tile-pending-approvals">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 32, height: 32, borderRadius: 8, background: '#1E3A8A', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Clock size={16} color="#60A5FA" />
            </div>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#F8FAFC' }}>Pending Approvals</div>
          </div>
          <span style={{ fontSize: 10, color: '#94A3B8' }}>{loading ? '…' : 'live'}</span>
        </div>
        <div style={{ fontSize: 44, fontWeight: 800, color: '#F8FAFC', marginTop: 10, lineHeight: 1 }}>{pending}</div>
        <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 4 }}>timesheet entries awaiting review</div>
        <button
          type="button"
          onClick={() => navigate('/admin/timesheets?approval_status=PENDING')}
          data-testid="tile-pending-review-btn"
          style={{
            marginTop: 'auto', background: '#1D4ED8', color: 'white', border: 'none',
            padding: '8px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600,
            display: 'inline-flex', alignItems: 'center', gap: 4, alignSelf: 'flex-start',
          }}
        >
          Review all <ChevronRight size={14} />
        </button>
      </div>

      {/* Overdue response windows */}
      <div className={tileBase} style={{ ...tileStyle, borderColor: overdue ? '#B45309' : undefined }} data-testid="tile-overdue">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: overdue ? '#78350F' : '#1E3A8A', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <AlertTriangle size={16} color={overdue ? '#FBBF24' : '#60A5FA'} />
          </div>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#F8FAFC' }}>Overdue Response (12h)</div>
        </div>
        <div style={{ fontSize: 44, fontWeight: 800, color: overdue ? '#FBBF24' : '#F8FAFC', marginTop: 10, lineHeight: 1 }}>{overdue}</div>
        <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 4 }}>operations not acknowledged in time</div>
        <div style={{ marginTop: 8, fontSize: 11, color: '#CBD5E1', display: 'grid', gap: 3 }}>
          {(stats?.overdue_operations || []).slice(0, 3).map(op => (
            <div key={op.id} style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{op.operation_title || op.id}</span>
              <span style={{ color: '#F59E0B', marginLeft: 8 }}>×{op.unresponded_count}</span>
            </div>
          ))}
        </div>
        <button
          type="button"
          onClick={() => navigate('/field-operations')}
          data-testid="tile-overdue-review-btn"
          style={{
            marginTop: 'auto', background: overdue ? '#B45309' : '#1D4ED8', color: 'white', border: 'none',
            padding: '8px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600,
            display: 'inline-flex', alignItems: 'center', gap: 4, alignSelf: 'flex-start',
          }}
        >
          Open operations <ChevronRight size={14} />
        </button>
      </div>

      {/* On-duty + hours this week */}
      <div className={tileBase} style={tileStyle} data-testid="tile-on-duty">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: '#064E3B', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Radio size={16} color="#34D399" />
          </div>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#F8FAFC' }}>On-Duty Now · This Week</div>
        </div>
        <div style={{ fontSize: 44, fontWeight: 800, color: '#34D399', marginTop: 10, lineHeight: 1 }}>{onDutyRows.length}</div>
        <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 4 }}>investigators clocked in</div>
        <div style={{ marginTop: 8, fontSize: 11, color: '#CBD5E1', display: 'grid', gap: 3, maxHeight: 110, overflowY: 'auto' }}>
          {weekRows.length === 0 ? (
            <div style={{ color: '#64748B' }}>No time logged this week yet.</div>
          ) : (
            weekRows.map(r => (
              <div key={r.investigator_id} style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.investigator_name}</span>
                <span style={{ color: '#F8FAFC', marginLeft: 8 }}>{fmtHM(r.total_seconds_week)}</span>
              </div>
            ))
          )}
        </div>
        <button
          type="button"
          onClick={() => navigate('/admin/timesheets')}
          data-testid="tile-on-duty-review-btn"
          style={{
            marginTop: 'auto', background: '#059669', color: 'white', border: 'none',
            padding: '8px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600,
            display: 'inline-flex', alignItems: 'center', gap: 4, alignSelf: 'flex-start',
          }}
        >
          Open timesheets <ChevronRight size={14} />
        </button>
      </div>
    </div>
  );
}
